import {
    Alert,
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from "@mui/material"
import { useEffect, useState } from "react"
import { unwrapResult } from "@reduxjs/toolkit"
import { useLocalization } from "@fluent/react"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"
import { IntervalUnitEnum } from "../../../common/commonTypes"
import { APIError } from "../../../../app/appTypes"
import { useAppDispatch } from "../../../../app/hooks"
import { CompanyDocumentTypeQmsSettings, updateQmsSettings } from "../companyDocumentTypesApi"

interface QmsSettingsDialogProps {
    open: boolean
    documentTypeId?: number
    settings?: CompanyDocumentTypeQmsSettings
    onClose: () => void
    onSuccessfullySaved?: (settings: CompanyDocumentTypeQmsSettings) => void
}

const sectionStyles = {
    p: 2,
    borderRadius: 2,
    borderColor: "divider",
    bgcolor: "background.paper",
}

export const QmsSettingsDialog = ({
    open,
    documentTypeId,
    settings,
    onClose,
    onSuccessfullySaved,
}: QmsSettingsDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const [localSettings, setLocalSettings] = useState<CompanyDocumentTypeQmsSettings | undefined>(settings)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<APIError | undefined>(undefined)

    useEffect(() => {
        setLocalSettings(settings)
        setError(undefined)
    }, [settings, open])

    const update = <K extends keyof CompanyDocumentTypeQmsSettings>(key: K, value: CompanyDocumentTypeQmsSettings[K]) => {
        setLocalSettings(prev => (prev ? { ...prev, [key]: value } : prev))
    }

    const handleSave = async () => {
        if (!documentTypeId || !localSettings) return

        setIsSaving(true)
        setError(undefined)

        try {
            const result = await dispatch(updateQmsSettings({ id: documentTypeId, dto: localSettings }))
            const payload = unwrapResult(result)
            onSuccessfullySaved?.(payload)
            onClose()
        } catch (err) {
            setError(err as APIError)
        } finally {
            setIsSaving(false)
        }
    }

    const intervalUnitLabel = (unit: IntervalUnitEnum) => {
        switch (unit) {
            case "DAYS": return l10n.getString("interval-unit-days", null, "Dni")
            case "MONTHS": return l10n.getString("interval-unit-months", null, "Miesiące")
            case "YEARS": return l10n.getString("interval-unit-years", null, "Lata")
        }
    }

    const hasValidityPeriod = localSettings
        ? localSettings.requiresEffectiveFrom || localSettings.requiresEffectiveTo
        : false

    const handleHasValidityPeriodChange = (checked: boolean) => {
        setLocalSettings(prev => prev ? {
            ...prev,
            requiresEffectiveFrom: checked,
            requiresEffectiveTo: checked,
        } : prev)
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                    bgcolor: "grey.100",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    fontWeight: 700,
                }}
            >
                {l10n.getString("company-document-type-qms-settings-title", null, "Ustawienia QMS dla typu dokumentu")}
            </DialogTitle>

            <DialogContent dividers>
                {localSettings && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {error && <Alert severity="error">{l10n.getString("error", null, "Wystąpił błąd podczas zapisu.")}</Alert>}

                        {/* Sekcja: Okres obowiązywania */}
                        <Paper variant="outlined" sx={sectionStyles}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                {l10n.getString("company-document-type-qms-validity-section", null, "Okres obowiązywania")}
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={hasValidityPeriod}
                                        onChange={e => handleHasValidityPeriodChange(e.target.checked)}
                                    />
                                }
                                label={l10n.getString("company-document-type-qms-has-validity-period", null, "Dokument ma termin obowiązywania")}
                            />

                            {hasValidityPeriod && (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1, pl: 1 }}>
                                    <Divider />

                                    {/* Data Od */}
                                    <FormControl>
                                        <FormLabel sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {l10n.getString("company-document-type-qms-effective-from-label", null, "Data obowiązuje od")}
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            value={localSettings.effectiveFromMode}
                                            onChange={e => update("effectiveFromMode", e.target.value as "MANUAL" | "PUBLICATION")}
                                        >
                                            <FormControlLabel
                                                value="MANUAL"
                                                control={<Radio />}
                                                label={l10n.getString("company-document-type-qms-effective-from-manual", null, "Manualna")}
                                            />
                                            <FormControlLabel
                                                value="PUBLICATION"
                                                control={<Radio />}
                                                label={l10n.getString("company-document-type-qms-effective-from-publication", null, "Zgodna z datą publikacji")}
                                            />
                                        </RadioGroup>
                                    </FormControl>

                                    <Divider />

                                    {/* Data Do */}
                                    <FormControl>
                                        <FormLabel sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {l10n.getString("company-document-type-qms-effective-to-label", null, "Data obowiązuje do")}
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            value={localSettings.effectiveToMode}
                                            onChange={e => update("effectiveToMode", e.target.value as "MANUAL" | "INTERVALFROM")}
                                        >
                                            <FormControlLabel
                                                value="MANUAL"
                                                control={<Radio />}
                                                label={l10n.getString("company-document-type-qms-effective-to-manual", null, "Manualna")}
                                            />
                                            <FormControlLabel
                                                value="INTERVALFROM"
                                                control={<Radio />}
                                                label={l10n.getString("company-document-type-qms-effective-to-interval", null, "Według interwału")}
                                            />
                                        </RadioGroup>
                                    </FormControl>

                                    {localSettings.effectiveToMode === "INTERVALFROM" && (
                                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                                            <TextField
                                                label={l10n.getString("company-document-type-qms-validity-interval-value", null, "Wartość interwału ważności")}
                                                type="number"
                                                value={localSettings.validityIntervalValue}
                                                onChange={e => update("validityIntervalValue", Number(e.target.value) || 0)}
                                                inputProps={{ min: 1 }}
                                                fullWidth
                                            />
                                            <TextField
                                                select
                                                label={l10n.getString("company-document-type-qms-validity-interval-unit", null, "Jednostka interwału ważności")}
                                                value={localSettings.validityIntervalUnit}
                                                onChange={e => update("validityIntervalUnit", e.target.value as IntervalUnitEnum)}
                                                fullWidth
                                            >
                                                {(["DAYS", "MONTHS", "YEARS"] as IntervalUnitEnum[]).map(unit => (
                                                    <MenuItem key={unit} value={unit}>{intervalUnitLabel(unit)}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>

                        {/* Sekcja: Przeglądy */}
                        <Paper variant="outlined" sx={sectionStyles}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                {l10n.getString("company-document-type-qms-review-section", null, "Przeglądy dokumentu")}
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={localSettings.requiresReview}
                                        onChange={e => update("requiresReview", e.target.checked)}
                                    />
                                }
                                label={l10n.getString("company-document-type-qms-requires-review", null, "Wymagane przeglądy dokumentu")}
                            />

                            {localSettings.requiresReview && (
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 1, pl: 1 }}>
                                    <TextField
                                        label={l10n.getString("company-document-type-qms-review-interval-value", null, "Wartość interwału przeglądu")}
                                        type="number"
                                        value={localSettings.reviewIntervalValue}
                                        onChange={e => update("reviewIntervalValue", Number(e.target.value) || 0)}
                                        inputProps={{ min: 1 }}
                                        fullWidth
                                    />
                                    <TextField
                                        select
                                        label={l10n.getString("company-document-type-qms-review-interval-unit", null, "Jednostka interwału przeglądu")}
                                        value={localSettings.reviewIntervalUnit}
                                        onChange={e => update("reviewIntervalUnit", e.target.value as IntervalUnitEnum)}
                                        fullWidth
                                    >
                                        {(["DAYS", "MONTHS", "YEARS"] as IntervalUnitEnum[]).map(unit => (
                                            <MenuItem key={unit} value={unit}>{intervalUnitLabel(unit)}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            )}
                        </Paper>

                        <Paper variant="outlined" sx={sectionStyles}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                {l10n.getString("company-document-type-qms-controlled-prints-section", null, "Śledzenie pobierania dokumentu")}
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!localSettings.requiresControlledPrints}
                                        onChange={e => update("requiresControlledPrints", e.target.checked)}
                                    />
                                }
                                label={l10n.getString("company-document-type-qms-requires-controlled-prints", null, "Wymagane śledzenie pobrań dokumentu")}
                            />
                        </Paper>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <SaveButton onClick={handleSave} loading={isSaving} disabled={isSaving || !documentTypeId || !localSettings} />
                <CancelButton onClick={onClose} loading={isSaving} />
            </DialogActions>
        </Dialog>
    )
}
